/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type * as Types from '../../../../__generated__/zeus/types';

import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
/** Arguments for creating a saved marketplace search. */
export type BuyerSavedSearchCreateInput = {
  /** Search definition to create for the viewer. */
  savedSearch: SavedSearchInput;
};

/** Arguments for deleting a saved marketplace search. */
export type BuyerSavedSearchDeleteInput = {
  /** Identifier of the saved search to delete. */
  id: Types.Scalars['NonEmptyString']['input'];
};

/** Arguments for updating a saved marketplace search. */
export type BuyerSavedSearchUpdateInput = {
  /** Identifier of the saved search to update. */
  id: Types.Scalars['NonEmptyString']['input'];
  /** Replacement search definition for the viewer. */
  savedSearch: SavedSearchInput;
};

/** Home criteria stored with a saved marketplace search. */
export type SavedSearchFiltersInput = {
  /** When true, include only homes marked eligible for FHA financing. */
  fhaEligibleOnly?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** When true, include only homes with a pool. */
  hasPoolOnly?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Maximum number of bathrooms; half bathrooms may be represented as .5. */
  maxBathrooms?: Types.Scalars['Float']['input'] | null | undefined;
  /** Maximum number of bedrooms. */
  maxBedrooms?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Maximum lot area in square feet. */
  maxLotSqft?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Maximum monthly homeowners association dues in whole US dollars. */
  maxMonthlyHoaFeeDollars?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Maximum list price in whole US dollars. */
  maxPriceDollars?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Maximum interior living area in square feet. */
  maxSqft?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Latest allowed construction year. */
  maxYearBuilt?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum number of bathrooms; half bathrooms may be represented as .5. */
  minBathrooms?: Types.Scalars['Float']['input'] | null | undefined;
  /** Minimum number of bedrooms. */
  minBedrooms?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum lot area in square feet. */
  minLotSqft?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum list price in whole US dollars. */
  minPriceDollars?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum interior living area in square feet. */
  minSqft?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Earliest allowed construction year. */
  minYearBuilt?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** When true, include only homes owned by Opendoor. */
  opendoorOwnedOnly?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Up to three home categories to include in the saved search. */
  propertyTypes?: Array<SavedSearchPropertyType> | null | undefined;
  /** When true, include only homes currently eligible for self-tour. */
  selfTourableOnly?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** When true, include only homes with a single-story layout. */
  singleStoryOnly?: Types.Scalars['Boolean']['input'] | null | undefined;
};

/** Name, geography, and home criteria for a saved marketplace search. */
export type SavedSearchInput = {
  /** Home criteria used when running the saved search. */
  filters: SavedSearchFiltersInput;
  /** Geographic scope used when running the saved search. */
  location: SavedSearchLocationInput;
  /** Customer-provided name for the saved search, up to 120 characters. */
  name: Types.Scalars['NonEmptyString']['input'];
};

/** Geographic scope for a saved search; provide a market, zones, or polygon vertices. */
export type SavedSearchLocationInput = {
  /** Market identifier included in the saved search. */
  market?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** Up to 500 polygon vertices defining the saved search area in decimal degrees. */
  vertices?: Array<SavedSearchPointInput> | null | undefined;
  /** Up to 200 IDs of geographic zones included in the saved search. */
  zoneIds?: Array<Types.Scalars['UUID']['input']> | null | undefined;
};

/** Geographic coordinate in decimal degrees used by a saved search. */
export type SavedSearchPointInput = {
  /** Latitude in decimal degrees, from -90 to 90. */
  lat: Types.Scalars['Latitude']['input'];
  /** Longitude in decimal degrees, from -180 to 180. */
  lon: Types.Scalars['Longitude']['input'];
};

/** Home categories that can be included in a saved search. */
export type SavedSearchPropertyType =
  /** A condominium home. */
  | 'CONDO'
  /** A house or other single-family home. */
  | 'HOME'
  /** A townhome. */
  | 'TOWNHOME';

export type SavedSearchFieldsFragment = { id: Types.Scalars['ID']['output'], name: Types.Scalars['String']['output'], location: { market: Types.Scalars['NonEmptyString']['output'] | null, zoneIds: Array<Types.Scalars['UUID']['output']>, vertices: Array<{ lat: Types.Scalars['Latitude']['output'], lon: Types.Scalars['Longitude']['output'] }>, zones: Array<{ name: Types.Scalars['NonEmptyString']['output'] }> }, filters: { minPriceDollars: Types.Scalars['SafeInt']['output'] | null, maxPriceDollars: Types.Scalars['SafeInt']['output'] | null, maxMonthlyHoaFeeDollars: Types.Scalars['SafeInt']['output'] | null, minBedrooms: Types.Scalars['SafeInt']['output'] | null, maxBedrooms: Types.Scalars['SafeInt']['output'] | null, minBathrooms: Types.Scalars['Float']['output'] | null, maxBathrooms: Types.Scalars['Float']['output'] | null, minSqft: Types.Scalars['SafeInt']['output'] | null, maxSqft: Types.Scalars['SafeInt']['output'] | null, minLotSqft: Types.Scalars['SafeInt']['output'] | null, maxLotSqft: Types.Scalars['SafeInt']['output'] | null, minYearBuilt: Types.Scalars['SafeInt']['output'] | null, maxYearBuilt: Types.Scalars['SafeInt']['output'] | null, opendoorOwnedOnly: Types.Scalars['Boolean']['output'], selfTourableOnly: Types.Scalars['Boolean']['output'], singleStoryOnly: Types.Scalars['Boolean']['output'], hasPoolOnly: Types.Scalars['Boolean']['output'], fhaEligibleOnly: Types.Scalars['Boolean']['output'], propertyTypes: Array<Types.SavedSearchPropertyType> } };

export type MarketplaceSavedSearchesQueryVariables = Exact<{ [key: string]: never; }>;


export type MarketplaceSavedSearchesQuery = { customerAccount: { buyer: { savedSearches: Array<{ id: Types.Scalars['ID']['output'], name: Types.Scalars['String']['output'], location: { market: Types.Scalars['NonEmptyString']['output'] | null, zoneIds: Array<Types.Scalars['UUID']['output']>, vertices: Array<{ lat: Types.Scalars['Latitude']['output'], lon: Types.Scalars['Longitude']['output'] }>, zones: Array<{ name: Types.Scalars['NonEmptyString']['output'] }> }, filters: { minPriceDollars: Types.Scalars['SafeInt']['output'] | null, maxPriceDollars: Types.Scalars['SafeInt']['output'] | null, maxMonthlyHoaFeeDollars: Types.Scalars['SafeInt']['output'] | null, minBedrooms: Types.Scalars['SafeInt']['output'] | null, maxBedrooms: Types.Scalars['SafeInt']['output'] | null, minBathrooms: Types.Scalars['Float']['output'] | null, maxBathrooms: Types.Scalars['Float']['output'] | null, minSqft: Types.Scalars['SafeInt']['output'] | null, maxSqft: Types.Scalars['SafeInt']['output'] | null, minLotSqft: Types.Scalars['SafeInt']['output'] | null, maxLotSqft: Types.Scalars['SafeInt']['output'] | null, minYearBuilt: Types.Scalars['SafeInt']['output'] | null, maxYearBuilt: Types.Scalars['SafeInt']['output'] | null, opendoorOwnedOnly: Types.Scalars['Boolean']['output'], selfTourableOnly: Types.Scalars['Boolean']['output'], singleStoryOnly: Types.Scalars['Boolean']['output'], hasPoolOnly: Types.Scalars['Boolean']['output'], fhaEligibleOnly: Types.Scalars['Boolean']['output'], propertyTypes: Array<Types.SavedSearchPropertyType> } }> } } };

export type MarketplaceCreateSavedSearchMutationVariables = Exact<{
  input: Types.BuyerSavedSearchCreateInput;
}>;


export type MarketplaceCreateSavedSearchMutation = { buyerSavedSearchCreate:
    | { __typename: 'InvalidInputError', message: Types.Scalars['String']['output'] }
    | { __typename: 'NotFoundError', message: Types.Scalars['String']['output'] }
    | { __typename: 'SavedSearch', id: Types.Scalars['ID']['output'], name: Types.Scalars['String']['output'], location: { market: Types.Scalars['NonEmptyString']['output'] | null, zoneIds: Array<Types.Scalars['UUID']['output']>, vertices: Array<{ lat: Types.Scalars['Latitude']['output'], lon: Types.Scalars['Longitude']['output'] }>, zones: Array<{ name: Types.Scalars['NonEmptyString']['output'] }> }, filters: { minPriceDollars: Types.Scalars['SafeInt']['output'] | null, maxPriceDollars: Types.Scalars['SafeInt']['output'] | null, maxMonthlyHoaFeeDollars: Types.Scalars['SafeInt']['output'] | null, minBedrooms: Types.Scalars['SafeInt']['output'] | null, maxBedrooms: Types.Scalars['SafeInt']['output'] | null, minBathrooms: Types.Scalars['Float']['output'] | null, maxBathrooms: Types.Scalars['Float']['output'] | null, minSqft: Types.Scalars['SafeInt']['output'] | null, maxSqft: Types.Scalars['SafeInt']['output'] | null, minLotSqft: Types.Scalars['SafeInt']['output'] | null, maxLotSqft: Types.Scalars['SafeInt']['output'] | null, minYearBuilt: Types.Scalars['SafeInt']['output'] | null, maxYearBuilt: Types.Scalars['SafeInt']['output'] | null, opendoorOwnedOnly: Types.Scalars['Boolean']['output'], selfTourableOnly: Types.Scalars['Boolean']['output'], singleStoryOnly: Types.Scalars['Boolean']['output'], hasPoolOnly: Types.Scalars['Boolean']['output'], fhaEligibleOnly: Types.Scalars['Boolean']['output'], propertyTypes: Array<Types.SavedSearchPropertyType> } }
    | { __typename: 'UnauthorizedError', message: Types.Scalars['String']['output'] }
   };

export type MarketplaceUpdateSavedSearchMutationVariables = Exact<{
  input: Types.BuyerSavedSearchUpdateInput;
}>;


export type MarketplaceUpdateSavedSearchMutation = { buyerSavedSearchUpdate:
    | { __typename: 'InvalidInputError', message: Types.Scalars['String']['output'] }
    | { __typename: 'NotFoundError', message: Types.Scalars['String']['output'] }
    | { __typename: 'SavedSearch', id: Types.Scalars['ID']['output'], name: Types.Scalars['String']['output'], location: { market: Types.Scalars['NonEmptyString']['output'] | null, zoneIds: Array<Types.Scalars['UUID']['output']>, vertices: Array<{ lat: Types.Scalars['Latitude']['output'], lon: Types.Scalars['Longitude']['output'] }>, zones: Array<{ name: Types.Scalars['NonEmptyString']['output'] }> }, filters: { minPriceDollars: Types.Scalars['SafeInt']['output'] | null, maxPriceDollars: Types.Scalars['SafeInt']['output'] | null, maxMonthlyHoaFeeDollars: Types.Scalars['SafeInt']['output'] | null, minBedrooms: Types.Scalars['SafeInt']['output'] | null, maxBedrooms: Types.Scalars['SafeInt']['output'] | null, minBathrooms: Types.Scalars['Float']['output'] | null, maxBathrooms: Types.Scalars['Float']['output'] | null, minSqft: Types.Scalars['SafeInt']['output'] | null, maxSqft: Types.Scalars['SafeInt']['output'] | null, minLotSqft: Types.Scalars['SafeInt']['output'] | null, maxLotSqft: Types.Scalars['SafeInt']['output'] | null, minYearBuilt: Types.Scalars['SafeInt']['output'] | null, maxYearBuilt: Types.Scalars['SafeInt']['output'] | null, opendoorOwnedOnly: Types.Scalars['Boolean']['output'], selfTourableOnly: Types.Scalars['Boolean']['output'], singleStoryOnly: Types.Scalars['Boolean']['output'], hasPoolOnly: Types.Scalars['Boolean']['output'], fhaEligibleOnly: Types.Scalars['Boolean']['output'], propertyTypes: Array<Types.SavedSearchPropertyType> } }
    | { __typename: 'UnauthorizedError', message: Types.Scalars['String']['output'] }
   };

export type MarketplaceDeleteSavedSearchMutationVariables = Exact<{
  input: Types.BuyerSavedSearchDeleteInput;
}>;


export type MarketplaceDeleteSavedSearchMutation = { buyerSavedSearchDelete:
    | { __typename: 'CommandResult', success: Types.Scalars['Boolean']['output'] }
    | { __typename: 'InvalidInputError', message: Types.Scalars['String']['output'] }
    | { __typename: 'NotFoundError', message: Types.Scalars['String']['output'] }
    | { __typename: 'UnauthorizedError', message: Types.Scalars['String']['output'] }
   };

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>['__apiType']>;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}
export const SavedSearchFieldsFragmentDoc = /*#__PURE__*/ new TypedDocumentString(`
    fragment SavedSearchFields on SavedSearch {
  id
  name
  location {
    market
    zoneIds
    vertices {
      lat
      lon
    }
    zones {
      name
    }
  }
  filters {
    minPriceDollars
    maxPriceDollars
    maxMonthlyHoaFeeDollars
    minBedrooms
    maxBedrooms
    minBathrooms
    maxBathrooms
    minSqft
    maxSqft
    minLotSqft
    maxLotSqft
    minYearBuilt
    maxYearBuilt
    opendoorOwnedOnly
    selfTourableOnly
    singleStoryOnly
    hasPoolOnly
    fhaEligibleOnly
    propertyTypes
  }
}
    `, {"fragmentName":"SavedSearchFields"}) as unknown as TypedDocumentString<SavedSearchFieldsFragment, unknown>;
export const MarketplaceSavedSearchesDocument = /*#__PURE__*/ new TypedDocumentString(`
    query MarketplaceSavedSearches {
  customerAccount {
    buyer {
      savedSearches {
        ...SavedSearchFields
      }
    }
  }
}
    fragment SavedSearchFields on SavedSearch {
  id
  name
  location {
    market
    zoneIds
    vertices {
      lat
      lon
    }
    zones {
      name
    }
  }
  filters {
    minPriceDollars
    maxPriceDollars
    maxMonthlyHoaFeeDollars
    minBedrooms
    maxBedrooms
    minBathrooms
    maxBathrooms
    minSqft
    maxSqft
    minLotSqft
    maxLotSqft
    minYearBuilt
    maxYearBuilt
    opendoorOwnedOnly
    selfTourableOnly
    singleStoryOnly
    hasPoolOnly
    fhaEligibleOnly
    propertyTypes
  }
}`) as unknown as TypedDocumentString<MarketplaceSavedSearchesQuery, MarketplaceSavedSearchesQueryVariables>;
export const MarketplaceCreateSavedSearchDocument = /*#__PURE__*/ new TypedDocumentString(`
    mutation MarketplaceCreateSavedSearch($input: BuyerSavedSearchCreateInput!) {
  buyerSavedSearchCreate(input: $input) {
    __typename
    ... on SavedSearch {
      ...SavedSearchFields
    }
    ... on Error {
      message
    }
  }
}
    fragment SavedSearchFields on SavedSearch {
  id
  name
  location {
    market
    zoneIds
    vertices {
      lat
      lon
    }
    zones {
      name
    }
  }
  filters {
    minPriceDollars
    maxPriceDollars
    maxMonthlyHoaFeeDollars
    minBedrooms
    maxBedrooms
    minBathrooms
    maxBathrooms
    minSqft
    maxSqft
    minLotSqft
    maxLotSqft
    minYearBuilt
    maxYearBuilt
    opendoorOwnedOnly
    selfTourableOnly
    singleStoryOnly
    hasPoolOnly
    fhaEligibleOnly
    propertyTypes
  }
}`) as unknown as TypedDocumentString<MarketplaceCreateSavedSearchMutation, MarketplaceCreateSavedSearchMutationVariables>;
export const MarketplaceUpdateSavedSearchDocument = /*#__PURE__*/ new TypedDocumentString(`
    mutation MarketplaceUpdateSavedSearch($input: BuyerSavedSearchUpdateInput!) {
  buyerSavedSearchUpdate(input: $input) {
    __typename
    ... on SavedSearch {
      ...SavedSearchFields
    }
    ... on Error {
      message
    }
  }
}
    fragment SavedSearchFields on SavedSearch {
  id
  name
  location {
    market
    zoneIds
    vertices {
      lat
      lon
    }
    zones {
      name
    }
  }
  filters {
    minPriceDollars
    maxPriceDollars
    maxMonthlyHoaFeeDollars
    minBedrooms
    maxBedrooms
    minBathrooms
    maxBathrooms
    minSqft
    maxSqft
    minLotSqft
    maxLotSqft
    minYearBuilt
    maxYearBuilt
    opendoorOwnedOnly
    selfTourableOnly
    singleStoryOnly
    hasPoolOnly
    fhaEligibleOnly
    propertyTypes
  }
}`) as unknown as TypedDocumentString<MarketplaceUpdateSavedSearchMutation, MarketplaceUpdateSavedSearchMutationVariables>;
export const MarketplaceDeleteSavedSearchDocument = /*#__PURE__*/ new TypedDocumentString(`
    mutation MarketplaceDeleteSavedSearch($input: BuyerSavedSearchDeleteInput!) {
  buyerSavedSearchDelete(input: $input) {
    __typename
    ... on CommandResult {
      success
    }
    ... on Error {
      message
    }
  }
}
    `) as unknown as TypedDocumentString<MarketplaceDeleteSavedSearchMutation, MarketplaceDeleteSavedSearchMutationVariables>;